import {AudioPackCache} from './AudioPackCache';
import {parseVoicePath, VOICE_MAX_BYTES} from '../../static/mod/modSchema.js';

// Keep compressed blobs in IndexedDB; decode only the selected character.
export class VoiceModBank {
    constructor({readBlob, createHowl, createURL = blob => URL.createObjectURL(blob),
        revokeURL = url => URL.revokeObjectURL(url)}) {
        Object.assign(this, {readBlob, createHowl, createURL, revokeURL});
        this.entries = new Map();
        this.revision = 0;
        this.resetCache();
    }

    resetCache() {
        this.cache = new AudioPackCache(sprite => {
            const entry = this.entries.get(sprite);
            return this.createHowl({src: [entry.url], format: [entry.extension], preload: false, autoplay: false});
        });
    }

    replace(keys) {
        this.release();
        this.entries.clear();
        // Deterministic choice when a hand-made ZIP contains both formats.
        for (const key of [...keys].filter(key => typeof key === 'string').sort()) {
            const parsed = parseVoicePath(key);
            if (parsed && !this.entries.has(parsed.sprite)) this.entries.set(parsed.sprite, {...parsed, key});
        }
    }

    has(sprite) { return this.entries.has(sprite) && !this.entries.get(sprite).failed; }
    get(sprite) { return this.cache.get(sprite); }

    load(sprite) {
        const entry = this.entries.get(sprite);
        if (!entry || entry.failed) return Promise.resolve(null);
        if (entry.promise) return entry.promise;
        const revision = this.revision;
        const cache = this.cache;
        entry.promise = (async () => {
            try {
                const blob = await this.readBlob(entry.key);
                if (revision !== this.revision) return null;
                if (!(blob instanceof Blob) || !blob.size || blob.size > VOICE_MAX_BYTES) throw new Error('Invalid voice clip');
                entry.url = this.createURL(blob);
                const howl = await cache.load(sprite);
                if (revision !== this.revision) return null;
                if (!howl) throw new Error('Voice clip could not be decoded');
                return howl;
            } catch {
                if (revision === this.revision) {
                    entry.failed = true;
                    if (entry.url) this.revokeURL(entry.url);
                    entry.url = null;
                }
                return null;
            }
        })();
        return entry.promise;
    }

    release() {
        this.revision++;
        this.cache.stop();
        this.cache.releaseExcept(new Set());
        for (const entry of this.entries.values()) {
            if (entry.url) this.revokeURL(entry.url);
            entry.url = null;
            entry.promise = null;
        }
        this.resetCache();
    }
}
